export interface WorkflowItem {
  name: string;
  icon: string;
}

export interface McpItem {
  name: string;
  icon: string;
}

export interface SkillItem {
  name: string;
  icon: string;
}

export interface KnowledgeItem {
  name: string;
  icon: string;
}

export interface DatabaseItem {
  name: string;
  icon: string;
}

export interface MemoryConfig {
  capacity: number;
  retentionHours: number;
  updateFrequency: number;
}

export interface ExperimentDetail {
  id: string;
  model: string;
  modelIcon: string;
  agentType: string;
  workflows: WorkflowItem[];
  mcpTools: McpItem[];
  skills: SkillItem[];
  knowledgeBases: KnowledgeItem[];
  databases: DatabaseItem[];
  memory: MemoryConfig;
  openingStatement: string;
  suggestedQuestions: string[];
  agentConfig: string;
  previewContent: string;
}

export const experimentDetails: Record<string, ExperimentDetail> = {
  "1": {
    id: "1",
    model: "S1-Base-Pro",
    modelIcon: "ri-cpu-line",
    agentType: "自主规划Agent",
    workflows: [
      { name: "Inverse Design_workflow", icon: "ri-flow-chart" },
      { name: "MACE_workflow", icon: "ri-git-branch-line" },
    ],
    mcpTools: [
      { name: "Web_Scholar_Search", icon: "ri-search-2-line" },
      { name: "OpenReview", icon: "ri-article-line" },
    ],
    skills: [
      { name: "HEA_SearchCodebase", icon: "ri-code-s-slash-line" },
      { name: "HEA_CheckCommandStatus", icon: "ri-terminal-line" },
    ],
    knowledgeBases: [
      { name: "HEA催化剂设计10年研究文献梳理", icon: "ri-book-3-line" },
      { name: "人工智能技术赋能材料科学发展相关文献", icon: "ri-book-3-line" },
    ],
    databases: [
      { name: "paper_metadata", icon: "ri-database-line" },
      { name: "material_calculation_result", icon: "ri-database-line" },
      { name: "recommended_component", icon: "ri-database-line" },
    ],
    memory: { capacity: 10000, retentionHours: 24, updateFrequency: 12 },
    openingStatement: "我是一款由规划器驱动的多智能体系统（MAS），聚焦于自动化设计用于氢能析氢反应（Hydrogen Evolution Reaction, HER）的高活性高熵合金（High-Entropy Alloy, HEA）催化剂。",
    suggestedQuestions: [
      "输入自定义元素筛选标准模板与最大初始合金推荐数...",
      "如何优化HEA催化剂的合成路径？",
    ],
    agentConfig: `## ① 角色定义

**身份**：规划器驱动型多智能体系统（MAS），基于 S1-Base-Pro 模型构建

**定位**：面向材料领域科研人员的专业工具，专注于**催化析氢反应（HER）催化剂的自动化设计**

**价值**：提供通用且可扩展的自主材料发现框架，通过全周期逆向设计与梯度优化算法，已实现催化剂性能 27.7% 的提升

---

## ② 核心目标

1. **自动化设计**：从目标性能到材料方案的全流程设计，输出适用于 HER 的高活性 HEA 催化剂
2. **高通量筛选**：支持从自定义元素池逆向推导最大初始合金推荐数量，开展高通量组合筛选、性能预测与优化研究
3. **任务协同与智能报告**：支持多任务协同和智能报告，为科研人员提供实验预测与可视化报告

---

## ③ 核心能力与工具链

### 3.1 智能任务规划
- **基于 RAG 检索技术**：调用材料领域学术知识库，自动分解复杂任务为工具调用链，生成最优执行策略
- **内置知识库**：「HEA 催化剂设计近 10 年研究文献梳理」「人工智能技术赋能材料科学发展相关文献」

### 3.2 确定到材料逻辑设计
- 支持从标准性能（如低折叠过电位）出发，通过 Inverse Design_workflow 反向推导元素及 HEA 组成与结构
- 综合稳健优化算法，实现催化剂性能的迭代提升

### 3.3 集成先进计算模型
- **内嵌 MACE（Multi-scale Atomic Cluster Expansion）**机器学习势能模型（通过 MACE_workflow 触发），用于高效、高精度的原子尺度能量与结构预测
- **关联数据库**：material_calculation_result（材料计算结果）、recommended_component（推荐组分）

### 3.4 实时进度追踪与可视化
- 全流程推理报告：包含思考过程、工具执行状态、梯度优化集，支持可视化追踪与复现`,
    previewContent: `## 5. 催化剂稳定性与耐久性评估

最终组分(Ni,Fe,Mo,Pd,Pt)表现出卓越的耐久性特征：

1. **加速老化测试性能**：在 1M KOH 溶液中，经过 10000 次 CV 循环后：
   - 氢析出反应(HER)过电位仍能保持 ≤ 20 mV
   - 电化学活性面积(ECSA)衰减率 <10%
   - 塔菲尔斜率电化学稳定 ≤ 5mV/dec

2. **微观结构稳定机制**：
   - **多重化层保护**：表面自发形成的Ni(OH)₂/MoO₃/PtOx 多层结构可有效阻挡腐蚀侵蚀
   - **应变工程效益**：高熵合金的晶界效应 ≥ 5kJ/mol 显著改善了催化合金，抑制位错滑移与裂纹扩展
   - **电子结构稳定性**：Pd-Pt 改变层间d带中心位置调控在 -2.5 eV 至 -3.0 eV 区间，避免过度氧化或还原导致的相变

在 1M KOH 与 1M NaCl 的同等条件下，含Pt熵高合金经 10000 次 循环后活性最高率率仍能 5%，表明其具有工业级应用所所需的耐久性。

---

## 6. 实验验证与未来研究方向

**建议的电化学测量方法包括**：
- 采用三电极体系在 1M KOH 中测试过化后分析的极化曲线与塔菲尔斜率，重点关注 10 mA/cm² 电流密度下的性能差异
- 进行长时间电流测定（10 mA/cm² 恒定电流下连续运行 100 小时），监测电位漂移与溶液金属离子浓度

**理论计算建议**：
- 计算最优组分的强制附能（ΔG_H）与 零中心位置，检验Mo-Pd-Pt对电子结构的调制效果
- 基于现有 1,231 组联姻训练脉冲森林神经网络，预测新组合的活性位指标

**未来研究方向包括**：
- 非等摩尔比例研究：尝试编角 4:3:4:2:3 的配比，加增加 Pd 至 3~4 at.% 以增强同向性能
- 含微量元素调控：引入 B、P 等非金属元素优化配比调控电荷密度现阶段困难
- 动态数据集建设：定期更新合金分数据（如每年新增文献记录），保持计算模型的时效性`,
  },
  "2": {
    id: "2",
    model: "S1-Base-Pro",
    modelIcon: "ri-cpu-line",
    agentType: "自主规划Agent",
    workflows: [
      { name: "RL_Growth_workflow", icon: "ri-flow-chart" },
      { name: "Parameter_Optimize_workflow", icon: "ri-settings-3-line" },
    ],
    mcpTools: [
      { name: "Materials_Project_API", icon: "ri-search-2-line" },
      { name: "ArXiv_Search", icon: "ri-article-line" },
    ],
    skills: [
      { name: "GrowthSimulator", icon: "ri-code-s-slash-line" },
      { name: "PhaseAnalysis", icon: "ri-bar-chart-line" },
    ],
    knowledgeBases: [
      { name: "二维材料生长机制综述", icon: "ri-book-3-line" },
      { name: "强化学习材料科学应用文献", icon: "ri-book-3-line" },
    ],
    databases: [
      { name: "growth_parameters", icon: "ri-database-line" },
      { name: "material_phase_data", icon: "ri-database-line" },
    ],
    memory: { capacity: 8000, retentionHours: 48, updateFrequency: 6 },
    openingStatement: "我是一个专注于二维材料生长参数优化的强化学习智能体，能够自适应调控CVD、MBE等生长工艺参数，实现高质量二维材料的可控制备。",
    suggestedQuestions: [
      "请分析当前MoS₂生长参数对晶体质量的影响",
      "如何优化石墨烯的CVD生长温度曲线？",
    ],
    agentConfig: `## ① 角色定义

**身份**：强化学习驱动的二维材料生长参数优化系统

**定位**：面向材料合成领域研究人员，专注于二维材料（MoS₂、石墨烯、BN等）生长工艺的自适应调控

**价值**：通过强化学习算法持续优化生长参数，减少实验次数，提升材料晶体质量评分 35.2%

---

## ② 核心目标

1. **自适应参数调控**：实时调整温度、气压、气流等关键生长参数
2. **质量预测**：基于实验数据预测材料晶体质量与缺陷密度
3. **工艺优化**：构建最优生长窗口，指导实验方案设计`,
    previewContent: `## 4. 石墨烯CVD生长参数优化结果

经过强化学习算法 500 轮迭代优化，最优生长参数如下：

| 参数 | 初始值 | 优化值 | 提升 |
|------|--------|--------|------|
| 生长温度 | 1000°C | 1025°C | +2.5% |
| CH₄/H₂ 比例 | 0.1 | 0.085 | -15% |
| 生长时间 | 30min | 22min | -27% |

**质量评估**：
- 拉曼光谱 2D/G 峰比值：3.2（目标 ≥ 3.0）
- 载流子迁移率：15,000 cm²/V·s
- 缺陷密度：< 10¹⁰ cm⁻²`,
  },
  "3": {
    id: "3",
    model: "S1-Vision-Pro",
    modelIcon: "ri-eye-line",
    agentType: "多模态分析Agent",
    workflows: [
      { name: "Defect_Detection_workflow", icon: "ri-scan-line" },
      { name: "Perovskite_Optimize_workflow", icon: "ri-flow-chart" },
    ],
    mcpTools: [
      { name: "NREL_Database", icon: "ri-database-line" },
      { name: "DFT_Calculator", icon: "ri-calculator-line" },
    ],
    skills: [
      { name: "ImageDefectAnalysis", icon: "ri-image-line" },
      { name: "PCEPredictor", icon: "ri-line-chart-line" },
    ],
    knowledgeBases: [
      { name: "钙钛矿太阳能电池缺陷类型图谱", icon: "ri-book-3-line" },
      { name: "高效率太阳能电池材料数据库", icon: "ri-book-3-line" },
    ],
    databases: [
      { name: "perovskite_composition", icon: "ri-database-line" },
      { name: "defect_characterization", icon: "ri-database-line" },
      { name: "pce_records", icon: "ri-database-line" },
    ],
    memory: { capacity: 12000, retentionHours: 72, updateFrequency: 8 },
    openingStatement: "我是多模态AI辅助的钙钛矿材料分析系统，结合图像识别与理论计算，能够预测缺陷类型、浓度及其对光电转换效率的影响。",
    suggestedQuestions: [
      "上传SEM图像，分析晶粒边界缺陷分布",
      "预测Cs₀.₀₅FA₀.₈₅MA₀.₁PbI₃组分的PCE上限",
    ],
    agentConfig: `## ① 角色定义

**身份**：多模态AI辅助的钙钛矿太阳能电池材料分析系统

**定位**：结合视觉感知与计算物理，专注于钙钛矿材料缺陷表征与性能预测

**价值**：将缺陷识别准确率提升至 94.7%，PCE 预测误差降低至 ±0.3%

---

## ② 核心目标

1. **缺陷预测**：基于形貌图像自动识别晶粒边界、点缺陷、位错等
2. **组分优化**：推荐最优卤化物钙钛矿组分配比
3. **稳定性分析**：评估长期光照与湿度环境下的降解路径`,
    previewContent: `## 缺陷分析报告 - FA₀.₈₅MA₀.₁Cs₀.₀₅PbI₃

**样品编号**：PSC-2025-0142

### 检测到的主要缺陷类型：

1. **碘空位（V_I）密度**：3.2 × 10¹⁶ cm⁻³（中等风险）
2. **晶粒边界偏析**：PbI₂ 偏析率约 8.3%（需关注）
3. **MA⁺ 挥发空位**：0.7% 覆盖率（低风险）

### 性能预测：
- 理论最大 PCE：**24.8%**
- 当前实测 PCE：22.1%
- 主要损失来源：晶界复合（占总损失 67%）

**优化建议**：添加 0.5 mol% 的 PEAI 钝化晶界缺陷，预计提升 PCE 至 23.5%`,
  },
};

export const getExperimentDetail = (id: string): ExperimentDetail => {
  return experimentDetails[id] ?? {
    ...experimentDetails["1"],
    id,
    openingStatement: "这是一个智能科研实验助手，帮助您完成实验设计、数据分析和结果预测。",
  };
};
